import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

function stripSO(docNo) {
  if (!docNo) return "";
  return String(docNo).replace(/^SO/i, "").trim();
}

function pillClass(kind) {
  switch (kind) {
    case "ok":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "warn":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "bad":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
}

function Pill({ kind, children }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        pillClass(kind),
      ].join(" ")}
    >
      {children}
    </span>
  );
}

export default function StockWarehouseMatcher() {
  const [stockRows, setStockRows] = useState([]);
  const [warehouseRows, setWarehouseRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState("all"); // all | 49 | 50
  const [search, setSearch] = useState("");

  // NAV
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "stock_sales_live"),
      (snap) => {
        setStockRows(snap.docs.map((d) => d.data()));
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Warehouse
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "cylinders_warehouse"),
      (snap) => {
        setWarehouseRows(
          snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
      }
    );
    return () => unsub();
  }, []);

  // 🔥 Matching logic
  const matchedRows = useMemo(() => {
    const q = search.toLowerCase();

    return stockRows
      .map((nav) => {
        const serial = stripSO(nav.DocumentNo);
        if (!serial) return null;

        const wh = warehouseRows.find(
          (w) => String(w.SerialNumber) === String(serial)
        );

        if (!wh) return null;

        return {
          nav,
          warehouse: wh,
          serial,
        };
      })
      .filter(Boolean)
      .filter((row) => {
        if (filter === "49" && row.warehouse.Special !== "49") return false;
        if (filter === "50" && row.warehouse.Special !== "50") return false;

        const hay = [
          row.serial,
          row.nav.DocumentNo,
          row.warehouse.ProductId,
        ]
          .join(" ")
          .toLowerCase();

        return !q || hay.includes(q);
      });
  }, [stockRows, warehouseRows, filter, search]);

async function fixAllStockFlags() {
  if (!window.confirm("Are you sure you want to update ALL documents?")) return;

  try {
    for (const row of warehouseRows) {
      const special = String(row.Special || "");

      let newStock = null;

      if (special === "49") newStock = false;
      if (special === "50") newStock = true;

      // skip anything else
      if (newStock === null) continue;

      // only update if wrong (avoids unnecessary writes)
      if (row.Stock === newStock) continue;

      const ref = doc(db, "cylinders_warehouse", row.id);

      await updateDoc(ref, {
        Stock: newStock,
      });

      console.log(`Updated ${row.id} → Stock: ${newStock}`);
    }

    alert("Done fixing stock flags ✅");
  } catch (err) {
    console.error(err);
    alert("Error fixing stock flags");
  }
}

  // 🔧 Update function
  async function toggleSpecial(row) {
    const current = row.warehouse.Special;
    const next = current === "49" ? "50" : "49";

    const ref = doc(db, "cylinders_warehouse", row.warehouse.id);

    await updateDoc(ref, {
      Special: next,
    });
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex justify-between items-start gap-4">
        <div>
          <h1 className="text-xl font-semibold">
            Stock ↔ Warehouse Matcher
          </h1>

          <div className="mt-2 flex gap-2">
            <Pill kind="ok">Matches: {matchedRows.length}</Pill>
          </div>
        </div>

        <div className="w-full max-w-md">
          <input
            placeholder="Search serial, SO, product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm"
          />

          <div className="mt-2 flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className="px-3 py-1 border rounded"
            >
              All
            </button>

            <button
                onClick={fixAllStockFlags}
                className="bg-rose-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-rose-700"
                >
                Fix ALL Stock Flags (49/50)
            </button>

            <button
              onClick={() => setFilter("49")}
              className="px-3 py-1 border rounded"
            >
              Only 49
            </button>

            <button
              onClick={() => setFilter("50")}
              className="px-3 py-1 border rounded"
            >
              Only 50
            </button>
          </div>
        </div>
      </div>

      <div className="border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-3 text-left">Serial</th>
              <th className="p-3 text-left">SO</th>
              <th className="p-3 text-left">ProductId</th>
              <th className="p-3 text-left">Special</th>
              <th className="p-3 text-left">Action</th>
            </tr>
          </thead>

          <tbody>
            {matchedRows.map((row) => (
              <tr key={`${row.warehouse.id}__${row.nav.DocumentNo}`}>
                <td className="p-3 font-mono">{row.serial}</td>
                <td className="p-3 font-mono">
                  {row.nav.DocumentNo}
                </td>
                <td className="p-3 font-mono">
                  {row.warehouse.ProductId}
                </td>

                <td className="p-3">
                  <Pill
                    kind={
                      row.warehouse.Special === "49"
                        ? "warn"
                        : "ok"
                    }
                  >
                    {row.warehouse.Special}
                  </Pill>
                </td>

                <td className="p-3">
                  <button
                    onClick={() => toggleSpecial(row)}
                    className="bg-slate-900 text-white px-3 py-1 rounded"
                  >
                    Toggle →{" "}
                    {row.warehouse.Special === "49"
                      ? "50"
                      : "49"}
                  </button>
                </td>
              </tr>
            ))}

            {!loading && matchedRows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="p-6 text-center text-slate-500"
                >
                  No matches found
                </td>
              </tr>
            )}

            {loading && (
              <tr>
                <td colSpan={5} className="p-6 text-center">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}